import React from 'react';
import {
  Box, Card, CardContent, Typography, CircularProgress, Alert, Grid,
  Table, TableContainer, TableHead, TableRow, TableCell, TableBody, Button,
} from '@mui/material';
import { CheckCircle as CheckIcon, Cancel as RejectIcon } from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { verifyApi } from '@services/api/verify';

const COLORS = {
  card: '#1a2332', cardBorder: '#2a3444', muted: '#8b95a5',
  text: '#ffffff', green: '#00D084', red: '#FF4D4F', orange: '#FFA500',
};

const KPICard: React.FC<{ title: string; value: string | number; color?: string }> = ({ title, value, color = '#FF6B4A' }) => (
  <Card sx={{ backgroundColor: COLORS.card, borderLeft: `4px solid ${color}` }}>
    <CardContent sx={{ p: 2 }}>
      <Typography sx={{ fontSize: '0.75rem', color: COLORS.muted, fontWeight: 500, mb: 1 }}>{title}</Typography>
      <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: 'white' }}>{typeof value === 'number' ? value.toLocaleString() : value}</Typography>
    </CardContent>
  </Card>
);

export const VerifyPage: React.FC = () => {
  const queryClient = useQueryClient();

  const verifyQuery = useQuery({
    queryKey: ['verify'],
    queryFn: () => verifyApi.getVerifications(),
    staleTime: 30000,
  });

  const approveMutation = useMutation({
    mutationFn: (id: number) => verifyApi.approveRecord(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['verify'] }),
  });
  const rejectMutation = useMutation({
    mutationFn: (id: number) => verifyApi.rejectRecord(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['verify'] }),
  });

  const data = verifyQuery.data;
  const pending = data?.pending || [];

  return (
    <Box sx={{ pb: 4, color: COLORS.text }}>
      <Typography sx={{ fontSize: '0.9rem', fontWeight: 700, mb: 0.5 }}>Verify</Typography>
      <Typography sx={{ fontSize: '0.75rem', color: COLORS.muted, mb: 2 }}>
        Officers' "Correct the unregistered figure" submissions, awaiting your approval or rejection.
      </Typography>

      <Grid container spacing={1.5} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <KPICard title="Declared Pending" value={data?.pending_count ?? 0} color={COLORS.orange} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <KPICard title="Verified" value={data?.verified_count ?? 0} color={COLORS.green} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <KPICard title="Rejected" value={data?.rejected_count ?? 0} color={COLORS.red} />
        </Grid>
      </Grid>

      <Card sx={{ backgroundColor: COLORS.card, border: `1px solid ${COLORS.cardBorder}` }}>
        <Box sx={{ p: 1.5, borderBottom: `1px solid ${COLORS.cardBorder}` }}>
          <Typography sx={{ fontSize: '0.85rem', fontWeight: 700 }}>Pending corrections</Typography>
        </Box>
        {verifyQuery.isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress sx={{ color: COLORS.green }} /></Box>
        ) : verifyQuery.error ? (
          <Alert severity="error" sx={{ m: 2 }}>Failed to load pending corrections</Alert>
        ) : pending.length === 0 ? (
          <Typography sx={{ p: 3, color: COLORS.muted, textAlign: 'center' }}>Nothing pending — every submitted correction has been reviewed.</Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#2a3444' }}>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.68rem', color: COLORS.muted }}>WHEN</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.68rem', color: COLORS.muted }}>OFFICER</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.68rem', color: COLORS.muted }}>LOCATION</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.68rem', color: COLORS.muted }}>REPORTED</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.68rem', color: COLORS.muted }}>CORRECTED</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.68rem', color: COLORS.muted }}>REASON</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.68rem', color: COLORS.muted }}>ACTION</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pending.map((c) => {
                  const busy = (approveMutation.isPending && approveMutation.variables === c.id)
                    || (rejectMutation.isPending && rejectMutation.variables === c.id);
                  return (
                    <TableRow key={c.id} sx={{ borderBottom: `1px solid ${COLORS.cardBorder}`, '&:hover': { backgroundColor: '#252d3d' } }}>
                      <TableCell sx={{ fontSize: '0.78rem', color: COLORS.muted }}>
                        {new Date(c.created_at).toLocaleString('en-KE', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.78rem', color: COLORS.text, fontWeight: 600 }}>{c.officer_name}</TableCell>
                      <TableCell sx={{ fontSize: '0.78rem', color: COLORS.muted }}>
                        {[c.location, c.division, c.district, c.county].filter(Boolean).join(' / ')}
                      </TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.85rem', color: COLORS.muted }}>{c.reported_figure.toLocaleString()}</TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.85rem', fontWeight: 700, color: COLORS.orange }}>{c.corrected_figure.toLocaleString()}</TableCell>
                      <TableCell sx={{ fontSize: '0.75rem', color: COLORS.muted, maxWidth: 260 }}>{c.reason || '—'}</TableCell>
                      <TableCell align="center">
                        <Button
                          size="small" disabled={busy} onClick={() => approveMutation.mutate(c.id)}
                          startIcon={<CheckIcon sx={{ fontSize: 15 }} />}
                          sx={{ color: COLORS.green, minWidth: 0, mr: 0.5, textTransform: 'none', fontSize: '0.72rem' }}
                        >
                          Approve
                        </Button>
                        <Button
                          size="small" disabled={busy} onClick={() => rejectMutation.mutate(c.id)}
                          startIcon={<RejectIcon sx={{ fontSize: 15 }} />}
                          sx={{ color: COLORS.red, minWidth: 0, textTransform: 'none', fontSize: '0.72rem' }}
                        >
                          Reject
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>
    </Box>
  );
};
